/**
 * 相続税の速算表（万円単位）
 */
const TAX_BRACKETS = [
  { limit: 1000, rate: 0.10, deduction: 0 },        // 1,000万円以下: 10%
  { limit: 3000, rate: 0.15, deduction: 50 },       // 3,000万円以下: 15% - 50万円
  { limit: 5000, rate: 0.20, deduction: 200 },      // 5,000万円以下: 20% - 200万円
  { limit: 10000, rate: 0.30, deduction: 700 },     // 1億円以下: 30% - 700万円
  { limit: 20000, rate: 0.40, deduction: 1700 },    // 2億円以下: 40% - 1,700万円
  { limit: 30000, rate: 0.45, deduction: 2700 },    // 3億円以下: 45% - 2,700万円
  { limit: 60000, rate: 0.50, deduction: 4200 },    // 6億円以下: 50% - 4,200万円
  { limit: Infinity, rate: 0.55, deduction: 7200 }  // 6億円超: 55% - 7,200万円
];

/**
 * 最大公約数を計算
 *
 * @param {number} a - 数値A
 * @param {number} b - 数値B
 * @returns {number} 最大公約数
 */
function gcd(a, b) {
  return b === 0 ? a : gcd(b, a % b);
}

/**
 * 分数に変換
 *
 * @param {number} numerator - 分子
 * @param {number} denominator - 分母
 * @returns {string} 約分された分数（例: "1/2"）
 */
export function toFraction(numerator, denominator) {
  const divisor = gcd(numerator, denominator);
  return `${numerator / divisor}/${denominator / divisor}`;
}

/**
 * 基礎控除額を計算（万円単位）
 * 仕様書: docs/相続税計算仕様.md
 *
 * 基礎控除 = 3000万円 + (600万円 × 法定相続人数)
 *
 * @param {number} heirsCount - 法定相続人数
 * @returns {number} 基礎控除額（万円）
 */
function calculateBaseDeduction(heirsCount) {
  return 3000 + (600 * heirsCount);
}

/**
 * 速算表による税額を計算（万円単位）
 * 仕様書: docs/相続税計算仕様.md
 *
 * @param {number} amount - 法定相続分に応じた取得金額（万円）
 * @returns {number} 税額（万円）
 */
function calculateTaxByBracket(amount) {
  for (const bracket of TAX_BRACKETS) {
    if (amount <= bracket.limit) {
      return Math.floor(amount * bracket.rate - bracket.deduction);
    }
  }
  return 0;
}

/**
 * 相続税を計算（万円単位）
 * 仕様書: docs/相続税計算仕様.md
 *
 * 計算手順:
 * 1. 課税遺産総額を法定相続分で按分し、各相続人の税額を速算表で計算
 * 2. 各相続人の税額を合計して「相続税総額」を算出
 * 3. 相続税総額を「実際の取得割合」で按分して各相続人の納税額を決定
 * 4. 配偶者控除を適用
 *
 * @param {number} totalAssetsInManYen - 総資産額（万円）
 * @param {Array} legalHeirs - 法定相続分の相続人配列（税総額計算用）
 * @param {Array} actualHeirs - 実際の取得割合の相続人配列（按分用）
 * @param {boolean} hasSpouseHeir - 配偶者相続人がいるか
 * @returns {Object} { heirTaxes: [税額文字列配列], totalTax: 合計税額文字列 }
 */
function calculateInheritanceTax(totalAssetsInManYen, legalHeirs, actualHeirs, hasSpouseHeir) {
  // 法定相続人数
  const heirsCount = legalHeirs.length;

  // 基礎控除額
  const baseDeduction = calculateBaseDeduction(heirsCount);

  // 課税遺産総額
  const taxableAssets = totalAssetsInManYen - baseDeduction;

  // 課税遺産総額がマイナスの場合は相続税0円
  if (taxableAssets <= 0) {
    return {
      heirTaxes: actualHeirs.map(() => '0'),
      totalTax: '0'
    };
  }

  // ステップ1-2: 法定相続分に応じた取得金額で税額を計算し、相続税総額を算出
  // ※相続税の総額は常に法定相続分で計算する（カスタム割合でも変わらない）
  let grossTotalTax = 0;
  legalHeirs.forEach(heir => {
    const ratio = heir.ratioPercent / 100;
    const inheritedAmount = Math.floor(taxableAssets * ratio);
    const taxAmount = calculateTaxByBracket(inheritedAmount);
    grossTotalTax += taxAmount;
  });

  // ステップ3: 相続税総額を実際の取得割合で按分
  // 端数は切り上げ（同じ割合の相続人は同じ税額になる）
  const heirTaxAmounts = actualHeirs.map((heir) => {
    const ratio = heir.ratioPercent / 100;
    return Math.ceil(grossTotalTax * ratio);
  });

  // ステップ4: 配偶者控除を適用
  if (hasSpouseHeir) {
    // 配偶者は最初の相続人
    const spouseInheritance = parseFloat(actualHeirs[0].inheritance.replace(/,/g, ''));
    // 配偶者控除の判定は法定相続分を使用
    const legalSpouseRatio = legalHeirs[0].ratioPercent / 100;

    // 簡易計算: 配偶者の法定相続分または1.6億円までは非課税
    const spouseDeductionLimit = Math.max(16000, totalAssetsInManYen * legalSpouseRatio);

    if (spouseInheritance <= spouseDeductionLimit) {
      heirTaxAmounts[0] = 0;
    }
  }

  // 配偶者控除適用後の税額を合計
  const totalTaxAmount = heirTaxAmounts.reduce((sum, tax) => sum + tax, 0);

  // 文字列形式に変換
  const heirTaxes = heirTaxAmounts.map((amount) => {
    return amount.toLocaleString();
  });

  const totalTax = totalTaxAmount.toLocaleString();

  return { heirTaxes, totalTax };
}

/**
 * 相続人と相続割合を計算
 * 仕様書: docs/相続税計算仕様.md
 *
 * @param {Object} family - 家族構成データ
 * @param {number} totalAssetsInManYen - 総資産額（万円）
 * @param {Object} customRatios - カスタム割合（オプション）{ 0: 0.6, 1: 0.25, 2: 0.15 }
 * @returns {Object} { heirs: 相続人配列, hasSpouseHeir: 配偶者相続人フラグ }
 */
function calculateHeirs(family, totalAssetsInManYen, customRatios = null) {
  const heirs = [];
  const { spouse, children, father, mother, siblingsCount } = family;

  // 配偶者（健在の場合のみ）
  const hasSpouseHeir = spouse === 'alive';

  // 健在の子供
  const aliveChildren = children.filter(c => c.status === 'alive');

  // 代襲相続する孫がいる故人の子
  const deceasedChildrenWithGrandchildren = children.filter(c => c.status === 'deceased' && c.grandchildren > 0);

  // 代襲相続する孫の総数をカウント
  let totalGrandchildrenForSubstitution = 0;
  deceasedChildrenWithGrandchildren.forEach(c => {
    totalGrandchildrenForSubstitution += c.grandchildren;
  });

  // 法定相続人となる子の数（健在の子 + 孫がいる故人の子）
  const legalHeirChildrenCount = aliveChildren.length + deceasedChildrenWithGrandchildren.length;

  // 相続人の総数（健在の子供 + 代襲相続する孫）
  const totalDescendants = aliveChildren.length + totalGrandchildrenForSubstitution;

  // 相続順位の判定
  const hasFirstRankHeirs = totalDescendants > 0;
  const hasSecondRankHeirs = father === 'alive' || mother === 'alive';
  const hasThirdRankHeirs = siblingsCount > 0;

  // 適用される順位を決定
  let activeRank;
  if (hasFirstRankHeirs) {
    activeRank = 1;
  } else if (hasSecondRankHeirs) {
    activeRank = 2;
  } else if (hasThirdRankHeirs) {
    activeRank = 3;
  } else {
    activeRank = 0; // 配偶者のみ or 相続人なし
  }

  // 配偶者の相続割合を決定
  if (hasSpouseHeir) {
    let spouseRatio, spouseFraction;

    // カスタム割合が指定されている場合
    if (customRatios !== null && customRatios[0] !== undefined) {
      spouseRatio = Math.round(customRatios[0] * 100);
      spouseFraction = toFraction(spouseRatio, 100);
    } else if (activeRank === 0) {
      // 配偶者のみ → 100%
      spouseRatio = 100;
      spouseFraction = '1/1';
    } else if (activeRank === 1) {
      // 配偶者+子供/孫 → 配偶者は1/2
      spouseRatio = 50;
      spouseFraction = '1/2';
    } else if (activeRank === 2) {
      // 配偶者+父母 → 配偶者は2/3
      spouseRatio = 66;
      spouseFraction = '2/3';
    } else if (activeRank === 3) {
      // 配偶者+兄弟姉妹 → 配偶者は3/4
      spouseRatio = 75;
      spouseFraction = '3/4';
    }

    heirs.push({
      name: '配偶者',
      ratioPercent: spouseRatio,
      ratioFraction: spouseFraction,
      inheritance: Math.floor(totalAssetsInManYen * (spouseRatio / 100)).toLocaleString(),
      tax: '0'
    });
  }

  // 第1順位: 子供・孫の相続割合を計算
  if (activeRank === 1) {
    // 配偶者がいる場合、子供・孫は50%を分割、いない場合は100%を分割
    const descendantsShareTotal = hasSpouseHeir ? 50 : 100;

    // 法定相続人となる子の数で割る（代襲相続の原則）
    const perChildRatio = descendantsShareTotal / legalHeirChildrenCount;

    // 健在の子供
    aliveChildren.forEach((child) => {
      const childIndex = children.indexOf(child);
      const heirIndex = heirs.length;
      let ratioPercent, ratioFraction;

      if (customRatios !== null && customRatios[heirIndex] !== undefined) {
        ratioPercent = Math.round(customRatios[heirIndex] * 100);
        ratioFraction = toFraction(ratioPercent, 100);
      } else {
        ratioPercent = Math.floor(perChildRatio);
        let fractionDenominator = hasSpouseHeir ? legalHeirChildrenCount * 2 : legalHeirChildrenCount;
        ratioFraction = toFraction(1, fractionDenominator);
      }

      heirs.push({
        name: `子${childIndex + 1}`,
        ratioPercent: ratioPercent,
        ratioFraction: ratioFraction,
        inheritance: Math.floor(totalAssetsInManYen * (ratioPercent / 100)).toLocaleString(),
        tax: '※計算中'
      });
    });

    // 代襲相続する孫
    deceasedChildrenWithGrandchildren.forEach(child => {
      const childIndex = children.indexOf(child);
      const grandchildrenCount = child.grandchildren;

      for (let i = 0; i < grandchildrenCount; i++) {
        const heirIndex = heirs.length;
        let ratioPercent, ratioFraction;

        if (customRatios !== null && customRatios[heirIndex] !== undefined) {
          ratioPercent = Math.round(customRatios[heirIndex] * 100);
          ratioFraction = toFraction(ratioPercent, 100);
        } else {
          const perGrandchildRatio = perChildRatio / grandchildrenCount;
          ratioPercent = Math.floor(perGrandchildRatio);
          let fractionDenominator = hasSpouseHeir
            ? legalHeirChildrenCount * grandchildrenCount * 2
            : legalHeirChildrenCount * grandchildrenCount;
          ratioFraction = toFraction(1, fractionDenominator);
        }

        heirs.push({
          name: `孫（子${childIndex + 1}）`,
          ratioPercent: ratioPercent,
          ratioFraction: ratioFraction,
          inheritance: Math.floor(totalAssetsInManYen * (ratioPercent / 100)).toLocaleString(),
          tax: '※計算中'
        });
      }
    });
  }

  // 第2順位: 父母の相続割合を計算
  if (activeRank === 2) {
    // 配偶者がいる場合、父母は1/3を分割、いない場合は100%を分割
    const parentsShareTotal = hasSpouseHeir ? 34 : 100; // 34% ≈ 1/3（端数調整）

    // 健在な親の数
    const aliveParentsCount = (father === 'alive' ? 1 : 0) + (mother === 'alive' ? 1 : 0);
    const perParentRatio = parentsShareTotal / aliveParentsCount;

    if (father === 'alive') {
      const heirIndex = heirs.length;
      let ratioPercent, ratioFraction;

      if (customRatios !== null && customRatios[heirIndex] !== undefined) {
        ratioPercent = Math.round(customRatios[heirIndex] * 100);
        ratioFraction = toFraction(ratioPercent, 100);
      } else {
        ratioPercent = Math.floor(perParentRatio);
        let fractionDenominator = hasSpouseHeir ? aliveParentsCount * 3 : aliveParentsCount;
        ratioFraction = toFraction(1, fractionDenominator);
      }

      heirs.push({
        name: '父',
        ratioPercent: ratioPercent,
        ratioFraction: ratioFraction,
        inheritance: Math.floor(totalAssetsInManYen * (ratioPercent / 100)).toLocaleString(),
        tax: '※計算中'
      });
    }

    if (mother === 'alive') {
      const heirIndex = heirs.length;
      let ratioPercent, ratioFraction;

      if (customRatios !== null && customRatios[heirIndex] !== undefined) {
        ratioPercent = Math.round(customRatios[heirIndex] * 100);
        ratioFraction = toFraction(ratioPercent, 100);
      } else {
        ratioPercent = Math.floor(perParentRatio);
        let fractionDenominator = hasSpouseHeir ? aliveParentsCount * 3 : aliveParentsCount;
        ratioFraction = toFraction(1, fractionDenominator);
      }

      heirs.push({
        name: '母',
        ratioPercent: ratioPercent,
        ratioFraction: ratioFraction,
        inheritance: Math.floor(totalAssetsInManYen * (ratioPercent / 100)).toLocaleString(),
        tax: '※計算中'
      });
    }
  }

  // 第3順位: 兄弟姉妹の相続割合を計算
  if (activeRank === 3) {
    // 配偶者がいる場合、兄弟姉妹は1/4を分割、いない場合は100%を分割
    const siblingsShareTotal = hasSpouseHeir ? 25 : 100;
    const perSiblingRatio = siblingsShareTotal / siblingsCount;

    for (let i = 0; i < siblingsCount; i++) {
      const heirIndex = heirs.length;
      let ratioPercent, ratioFraction;

      if (customRatios !== null && customRatios[heirIndex] !== undefined) {
        ratioPercent = Math.round(customRatios[heirIndex] * 100);
        ratioFraction = toFraction(ratioPercent, 100);
      } else {
        ratioPercent = Math.floor(perSiblingRatio);
        let fractionDenominator = hasSpouseHeir ? siblingsCount * 4 : siblingsCount;
        ratioFraction = toFraction(1, fractionDenominator);
      }

      heirs.push({
        name: `兄弟${i + 1}`,
        ratioPercent: ratioPercent,
        ratioFraction: ratioFraction,
        inheritance: Math.floor(totalAssetsInManYen * (ratioPercent / 100)).toLocaleString(),
        tax: '※計算中'
      });
    }
  }

  return { heirs, hasSpouseHeir };
}

/**
 * 総資産額を計算（万円単位）
 * 仕様書: docs/相続税計算仕様.md
 *
 * @param {Object} assets - 資産データ
 * @param {number} heirsCount - 法定相続人数
 * @returns {Object} { grossTotal: 単純合計, netTotal: 非課税枠適用後 }
 */
function calculateTotalAssets(assets, heirsCount) {
  let grossTotal = 0;  // 単純合計（表示用）
  let netTotal = 0;    // 非課税枠適用後（税計算用）

  for (const [key, value] of Object.entries(assets)) {
    const amount = parseFloat(value) || 0;

    if (key === 'insurance') {
      // 保険金: 単純合計には全額、課税対象には非課税枠適用後
      grossTotal += amount;
      const exemption = 500 * heirsCount;
      netTotal += Math.max(0, amount - exemption);
    } else if (key === 'retirement') {
      // 退職金: 単純合計には全額、課税対象には非課税枠適用後
      grossTotal += amount;
      const exemption = 500 * heirsCount;
      netTotal += Math.max(0, amount - exemption);
    } else if (key === 'debt') {
      // 借金: マイナス計上
      grossTotal -= amount;
      netTotal -= amount;
    } else {
      // その他: そのままプラス計上
      grossTotal += amount;
      netTotal += amount;
    }
  }

  return { grossTotal, netTotal };
}

/**
 * 相続計算（相続人・相続割合・相続税）
 * 仕様書: docs/相続税計算仕様.md
 *
 * @param {Object} assets - 資産データ
 * @param {Object} family - 家族構成データ
 * @param {Object} options - オプション { customRatios: { 0: 0.6, 1: 0.25, 2: 0.15 } }
 * @returns {Object} 計算結果 { grossTotalAssets, totalAssets, heirs: { heirs, hasSpouseHeir }, totalTax }
 */
export default function calculateInheritance(assets, family, options = {}) {
  const { customRatios = null } = options;

  // 法定相続人数を先に計算（保険金・退職金の非課税枠計算に必要）
  const tempHeirs = calculateHeirs(family, 0, null); // 法定相続分で計算
  const heirsCount = tempHeirs.heirs.length;

  // 総資産額を計算
  const { grossTotal, netTotal } = calculateTotalAssets(assets, heirsCount);

  // 法定相続分の相続人を計算（税総額計算用）
  const legalHeirsResult = calculateHeirs(family, netTotal, null);

  // 実際の相続人を計算（カスタム割合がある場合はそれを使用）
  const actualHeirsResult = customRatios
    ? calculateHeirs(family, netTotal, customRatios)
    : legalHeirsResult;

  // 相続税を計算（法定相続分で総額を計算し、実際の割合で按分）
  const taxResult = calculateInheritanceTax(
    netTotal,
    legalHeirsResult.heirs,   // 税総額計算用（法定相続分）
    actualHeirsResult.heirs,  // 按分用（実際の割合）
    actualHeirsResult.hasSpouseHeir
  );

  // 各相続人のデータに税額をマージ
  const heirsWithTax = actualHeirsResult.heirs.map((heir, index) => ({
    ...heir,
    tax: taxResult.heirTaxes[index]
  }));

  return {
    grossTotalAssets: grossTotal,  // 単純合計（表示用）
    totalAssets: netTotal,         // 非課税枠適用後（税計算用・従来互換）
    heirs: {
      heirs: heirsWithTax,
      hasSpouseHeir: actualHeirsResult.hasSpouseHeir
    },
    totalTax: taxResult.totalTax
  };
}
